import { useEffect, useState } from "react"

import { Flex, Form, Input, Space, Typography } from "antd"
import { useTranslation } from "react-i18next"

import ButtonComponent from "../../../Components/Button"
import PasswordValidator from "../../../Components/PasswordValidator"
import Icon from "../../../Icon"

const { Text } = Typography

function ChangePasswordForm({
  userError,
  saveResult,
  userSaveResultReset,
  onClose,
  onSubmit,
  userErrorsReset,
  onChangeModal,
  setField,
  sendCodePassword,
  isMobile,
  saveFetching
}) {
  const { t } = useTranslation()

  const [errorText, setErrorText] = useState(null)

  const [currentError, setCurrentError] = useState({
    text: null,
    error: false
  })

  const [values, setValues] = useState({
    current: null,
    password: null,
    confirm: null
  })

  const [form] = Form.useForm()

  useEffect(() => {
    if (userError) {
      if (typeof userError === "string") {
        if (userError.length > 100) {
          setErrorText(t("Server response error"))
        } else {
          setErrorText(userError)
        }
      } else {
        if (userError?.type === "current_password") {
          setCurrentError({ text: userError.detail, error: true })
        } else {
          setErrorText(Object.values(userError)[0])
        }
      }
    }
  }, [userError])

  useEffect(() => {
    clearFormData()
    if (saveResult?.create) {
      onClose()
    }
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [saveResult])

  const onFinish = (value) => {
    const data = { ...value }
    userErrorsReset()
    onSubmit(data.current, data.password)
  }
  const handleClose = () => {
    clearFormData()
    onClose()
  }

  const onFinishFailed = ({ errorFields }) => {
    if (errorFields) {
      setErrorText(errorFields[0].errors)
    }
  }

  function clearFormData() {
    form.resetFields()
    setValues({ current: null, password: null, confirm: null })
    setErrorText(null)
    setCurrentError({ text: null, error: false })
    userErrorsReset()
    userSaveResultReset()
  }

  function handleClearError(e, field) {
    form.setFields([
      {
        name: field,
        errors: []
      }
    ])
    setErrorText(null)
  }

  function onFocusHandler(e) {
    handleClearError(e, e.target.name)
  }

  function handleChange(e) {
    const { name, value } = e.target
    if (name === "current") {
      userErrorsReset()
      setCurrentError({ text: null, error: false })
    }
    setValues({ ...values, [name]: value })
  }

  function forgotPassword() {
    userErrorsReset()
    setField("")
    onChangeModal("forgotPassword")
    sendCodePassword()
  }

  return (
    <Form
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      validateTrigger="onSubmit"
      layout="vertical"
      form={form}
    >
      <Form.Item
        label={t("Current password")}
        name="current"
        rules={[{ required: true, message: t("Required field") }]}
        validateStatus={currentError.error ? "error" : null}
      >
        <Form.Item name="current" noStyle>
          <Input.Password
            name="current"
            placeholder={t("Enter your current password")}
            autoComplete="current-password"
            visibilityToggle
            onChange={handleChange}
            onFocus={onFocusHandler}
          />
        </Form.Item>
        {currentError.text && (
          <Space>
            <Icon type={"alert_triangle_error"} />
            <Text type="danger">{currentError.text}</Text>
          </Space>
        )}
      </Form.Item>

      <Form.Item
        label={t("New password")}
        name="password"
        rules={[{ required: true, message: t("Required field") }]}
        validateStatus={errorText ? "error" : null}
      >
        <Form.Item name="password" noStyle>
          <Input.Password
            name="password"
            placeholder={t("Enter new password")}
            autoComplete="new-password"
            visibilityToggle
            onChange={handleChange}
            onFocus={onFocusHandler}
          />
        </Form.Item>
      </Form.Item>

      <PasswordValidator value={values.password} error={errorText} />

      <Form.Item
        label={t("Confirm new password")}
        name="confirm"
        validateStatus={errorText ? "error" : null}
        dependencies={["password"]}
        rules={[
          {
            required: true,
            message: t("Please confirm your password!")
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || !getFieldValue("password") || getFieldValue("password") === value) {
                return value?.length < 8
                  ? Promise.reject(t("Must be at least 8 characters"))
                  : Promise.resolve()
              }
              return Promise.reject(t("Passwords do not match"))
            }
          })
        ]}
      >
        <Input.Password
          name="confirm"
          placeholder={t("Confirm new password")}
          autoComplete="dontshow"
          visibilityToggle
          onChange={handleChange}
          onFocus={onFocusHandler}
        />
      </Form.Item>

      <Flex
        gap={16}
        justify="end"
        style={{
          padding: "0 3px 3px 0",
          flexDirection: isMobile && "column",
          paddingBottom: isMobile && 12
        }}
      >
        {!isMobile && (
          <ButtonComponent
            size="large"
            type="text"
            htmlType="button"
            className={"blue-text"}
            onClick={forgotPassword}
            style={{ marginRight: "auto", width: !isMobile ? 144 : "100%" }}
          >
            <Text style={{ color: "currentColor" }} ellipsis={{ tooltip: t("Forgot Password?") }}>
              {t("Forgot Password?")}
            </Text>
          </ButtonComponent>
        )}

        <ButtonComponent size="large" htmlType="button" onClick={handleClose}>
          {t("Cancel")}
        </ButtonComponent>

        <ButtonComponent
          size="large"
          type="primary"
          htmlType="submit"
          loading={saveFetching}
          disabled={!values.confirm || !values.current}
        >
          {t("Change password")}
        </ButtonComponent>
      </Flex>
    </Form>
  )
}

export default ChangePasswordForm
